import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Alert,
  Grid,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSnackbar } from '../Stateless/SnackBarProvider';
import SearchIcon from '@mui/icons-material/Search';
import { GridRowId } from '@mui/x-data-grid';
import CommonDataGrid from '../Stateless/CommonDataGrid';
import ErrorHandler from '../Stateless/Common/ErrorHandler';
import Loading from '../Stateless/Common/Loading';
import PermissionActionButtons from '../Stateless/Common/PermissionActionButtons';
import { usePermissions } from '../../context/PermissionContext';
import { atom, useSetAtom } from 'jotai';
import CouponForm from './CouponForm';
import {
  useBlockUnblockCouponById,
  useDeletCouponById,
  useGetAllCoupon,
} from '../Query/CouponQuery';
import EditStoreOwnerModal from './EditStoreOwnerModal';
import { categoryStyles } from '../Styles/CategoryFormStyles';
import { useManageWallet } from './ManageWallet/useManageWallet';
import { useDebounce } from './Utils/useDounce';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

type StoreItem = {
  id: string;
  couponCode: string;
  couponName: string;
  couponType: string;
  couponValue: string | number;
  maximumDiscount?: string | number; // UI field
  maxCouponAmount?: string | number; // API field
  couponStartDate: string;
  couponEndDate?: string;
  restaurants?: string[];
  restaurantIds: string[]; // Added for form compatibility
  couponPerUserLimit?: string | number;
  totalCouponCount?: string | number;
  minimumOrderValue?: string | number;
  sendEmailToAllUsers?: boolean;
  addedBy: string;
  status: string;
  couponValidity: number;
  couponCount: number;
};

type CouponListProps = {
  editCategory: (category: StoreItem) => void;
  addCoupon?: () => void;
};
export const categoryStoreAtom = atom<string | null>(null);

const CouponList: React.FC<CouponListProps> = ({
  editCategory,
}) => {
  const { showMessage } = useSnackbar();
  const { hasPermission, userType, permissions, isSubAdmin } = usePermissions();
  
  // Debug logging for permissions
  console.log('CouponList - User Type:', userType);
  console.log('CouponList - Is SubAdmin:', isSubAdmin);
  console.log('CouponList - Permissions:', permissions);
  console.log('CouponList - Has Add Permission:', hasPermission('coupon', 'Add'));
  console.log('CouponList - Has Edit Permission:', hasPermission('coupon', 'Edit'));
  console.log('CouponList - Has Delete Permission:', hasPermission('coupon', 'Delete'));
  console.log('CouponList - Has Block Permission:', hasPermission('coupon', 'Block'));
  console.log('CouponList - Has Unblock Permission:', hasPermission('coupon', 'Unblock'));
  
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage] = useState<string>('');
  const [snackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [editData, setEditData] = useState<any>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);

  const { params, dispatch } = useManageWallet();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);
  const { data: CouponList, isLoading, refetch } = useGetAllCoupon(memoizedParams);

  console.log(CouponList)

  const setCategoryStoreAtom = useSetAtom(categoryStoreAtom);
  const deleteCouponMutation = useDeletCouponById();
  const { mutateAsync: blockUnblockCouponAsync } = useBlockUnblockCouponById();

  const handleCloseAddModal = () => {
    setAddModalOpen(false);
    setEditData(null);
  };

  const handleAddCoupon = () => {
    console.log('handleAddCoupon called');
    setEditData(null);
    setAddModalOpen(true);
    console.log('addModalOpen set to true');
  };

  const formatDate = (
    params: { value: string | undefined | null } | string | undefined | null
  ) => {
    if (!params) return '';
    const dateString = typeof params === 'object' ? params.value : params;
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      const day = String(date.getDate()).padStart(2, '0');
      const month = date.toLocaleString('default', { month: 'short' }); // "May", "Jun", etc.
      const year = date.getFullYear();

      let hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12 || 12; // convert 0 -> 12 for 12-hour format

      return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  const handleEditCategory = (category: StoreItem) => {
    setEditData({
      id: category.id,
      couponCode: category.couponCode,
      couponName: category.couponName,
      couponType: category.couponType,
      couponValue: category.couponValue?.toString() || '',
      maximumDiscount:
        (category.maxCouponAmount !== undefined && category.maxCouponAmount !== null)
          ? String(category.maxCouponAmount)
          : (category.maximumDiscount !== undefined && category.maximumDiscount !== null)
            ? String(category.maximumDiscount)
            : '',
      couponStartDate: category.couponStartDate,
      couponEndDate: category.couponEndDate || '',
      // Backward compat: map store fields if present, else fall back
      stores: (category as any).stores || category.restaurants || [],
      storeIds: (category as any).storeIds || category.restaurantIds || [],
      restaurants: Array.isArray((category as any).stores) 
        ? (category as any).stores.map((store: any) => 
            typeof store === 'object' && store.storeName ? store.storeName : store
          )
        : (category.restaurants || []),
      restaurantIds: Array.isArray((category as any).stores)
        ? (category as any).stores.map((store: any) => 
            typeof store === 'object' && store.storeId ? store.storeId : store
          )
        : (category.restaurantIds || []),
      couponPerUserLimit: category.couponPerUserLimit?.toString() || '',
      totalCouponCount: category.totalCouponCount?.toString() || '',
      minimumOrderValue: category.minimumOrderValue?.toString() || '',
      sendEmailToAllUsers: category.sendEmailToAllUsers || false,
    });
    setAddModalOpen(true);
    editCategory(category);
  };


    const filteredRows = useMemo(() => {
      if (!CouponList?.data) return [];
      const storeOwners = CouponList?.data || []
      return storeOwners.map((row, index) => ({
        ...row,
        id: row.couponId,
        serialNumber: index + 1 + (Number(params.page - 1)) * Number(params.limit)
      }));
    }, [params.search, CouponList, params.page, params.limit])


  const handleBlock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Coupon selected to block', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to block ${selectedCategoryIds.length} Coupon(s)?`
      )
    ) {
      try {
        await blockUnblockCouponAsync({
          id: selectedCategoryIds,
          data: { status: 'blocked' },
        });
        setSelectedCategoryIds([]);
        showMessage('Coupon blocked successfully', 'success');
        refetch()
      } catch {
        showMessage('Failed to Coupon', 'error');
      }
    }
  };

  const handleUnblock = async () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Coupon selected to unblock', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to unblock ${selectedCategoryIds.length} Coupon(s)?`
      )
    ) {
      try {
        await blockUnblockCouponAsync({
          id: selectedCategoryIds,
          data: { status: 'unblocked' },
        });
        setSelectedCategoryIds([]);
        showMessage('Coupon unblocked successfully', 'success');
        refetch()
      } catch {
        showMessage('Failed to unblock Coupon', 'error');
      }
    }
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this Coupon?')) {
      deleteCouponMutation.mutate([id], {
        onSuccess: () => {
          setSelectedCategoryIds([]);
        },
      });
      refetch()
    }
  };

  const handleDeleteSelected = () => {
    if (selectedCategoryIds.length === 0) {
      showMessage('No Coupon selected to delete', 'error');
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedCategoryIds.length} Coupon(s)?`
      )
    ) {
      deleteCouponMutation.mutate(selectedCategoryIds, {
        onSuccess: () => {
          setSelectedCategoryIds([]);
          showMessage(
            `${selectedCategoryIds.length} Coupon(s) deleted successfully`,
            'success'
          );
          refetch()
        },
        onError: (error: any) => {
          showMessage(error?.message || 'Failed to delete Coupon', 'error');
        },
      });
    }
  };


   const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    };
  
    useEffect(() => {
      dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
    }, [debouncedSearch]);


  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };



  // Determine bulk visibility based on selection statuses
  const { showBlock, showUnblock } = useMemo(() => {
    if (!selectedCategoryIds || selectedCategoryIds.length === 0) return { showBlock: false, showUnblock: false };
    const dataList = CouponList?.data || [];
    const idToStatus = new Map<string, string>(
      dataList.map((item: any) => [String(item.couponId), String(item.status || '')])
    );
    const statuses = selectedCategoryIds.map((id) => idToStatus.get(String(id)) || '');
    const hasBlocked = statuses.some((s) => s === 'blocked');
    const hasUnblocked = statuses.some((s) => s === 'unblocked');
    return { showBlock: hasUnblocked, showUnblock: hasBlocked };
  }, [selectedCategoryIds, CouponList]);

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const canEdit = hasPermission('coupon', 'Edit');
    const canDelete = hasPermission('coupon', 'Delete');
    if (!canEdit && !canDelete) return null;
    const id = typeof row.id === 'string' ? row.id : '';
    return (
      <>
        <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
          {canEdit && (
            <MenuItem onClick={() => { setAnchorEl(null); handleEditCategory(row); setCategoryStoreAtom(row.id); }}>
              <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem onClick={() => { setAnchorEl(null); if (id) handleDelete(id); }}>
              <ListItemIcon><DeleteIcon fontSize="small" /></ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns = [
    { field: 'serialNumber', headerName: 'S.No' },
    { field: 'couponCode', headerName: 'Coupon Code', flex: 1 },
    { field: 'couponName', headerName: 'Coupon Name', flex: 1 },
    { field: 'couponType', headerName: 'Type of Coupon', flex: 1 },
    { field: 'validityDays', headerName: 'Validity days' },
    {
      field: 'couponStartDate',
      headerName: 'Start Date',
      flex: 1,
      renderCell: (params: any) => formatDate(params.row.couponStartDate),
    },
    { field: 'totalCouponCount', headerName: 'coupon Count' },
    { field: 'addedBy', headerName: 'Added By', width: 100 },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => {
        const isBlocked = params.value === 'blocked';
        const id = params.row.id as string;
        const canBlock = hasPermission('coupon', 'Block');
        const canUnblock = hasPermission('coupon', 'Unblock');
        if ((isBlocked && !canUnblock) || (!isBlocked && !canBlock)) return null;
        return (
          <IconButton size="small" sx={{ color: isBlocked ? '#f44336' : '#4caf50' }} onClick={() => isBlocked ? blockUnblockCouponAsync({ id: [id], data: { status: 'unblocked' } }) : blockUnblockCouponAsync({ id: [id], data: { status: 'blocked' } })}>
            {isBlocked ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
          </IconButton>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: any) => <RowActions row={params.row} />,
    },
  ];

  const handleCategorySelection = (selectionModel: GridRowId[]) => {
    const ids = selectionModel.map((id) => String(id));
    setSelectedCategoryIds(ids);
  };

  console.log('Current addModalOpen state:', addModalOpen);
  console.log('Current editData:', editData);

  return (
    <Box sx={categoryStyles.ListContainer}>
      {snackbarOpen && (
        <Box sx={{ mb: 2 }}>
          <Alert
            severity={snackbarSeverity}
            onClose={() => setSnackbarOpen(false)}
          >
            {snackbarMessage}
          </Alert>
        </Box>
      )}

      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        sx={{ mb: 2 }}
      >
        <Box
          sx={{
            width: 600,
            border: '1px solid #ccc',
            borderRadius: '8px',
            background: '#fff',
          }}
        >
          <TextField
            fullWidth
            size="medium"
            placeholder="Search by Coupon"
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{
              '.MuiOutlinedInput-root': {
                height: 40,
                width: 600,
                border: 'none', // remove inner border
              },
              '& fieldset': {
                border: 'none', // remove Material-UI default border
              },
              '& .MuiInputBase-input': {
                paddingTop: '10px', // adjust this value to move the placeholder lower
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 20, color: '#aaa', mt: '4px' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Grid
          container
          justifyContent="flex-end"
          spacing={1}
          alignItems="center"
        >
          <Grid>
            <PermissionActionButtons
              module="coupon"
              onBlock={showBlock ? handleBlock : undefined}
              onUnblock={showUnblock ? handleUnblock : undefined}
              onDelete={selectedCategoryIds.length ? handleDeleteSelected : undefined}
            />
          </Grid>
          <Grid>
            {hasPermission('coupon', 'Add') && (
              <Button
                onClick={handleAddCoupon}
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
                startIcon={<Add sx={{ fontSize: 16}} />}
              >
                Add Coupon
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>

      <CommonDataGrid
      key={`datagrid-page-${params.page}-limit-${params.limit}`}
        rows={filteredRows|| []}
        columns={columns}
        pageSize={params.limit}
        loading={isLoading}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        page={params.page - 1}
        rowCount={CouponList?.pagination?.totalItems || 0}
        checkboxSelection
        selectionModel={selectedCategoryIds}
        onRowSelectionModelChange={handleCategorySelection}
        components={{
          LoadingOverlay: Loading,
          NoRowsOverlay: () => (
            <ErrorHandler
              type="no-data"
              message="No categories found"
              onRetry={() => refetch()}
            />
          ),
        }}
      />
      {addModalOpen && (
        <EditStoreOwnerModal
          open={addModalOpen}
          onClose={handleCloseAddModal}
          title={editData ? 'Edit Coupon' : 'Add Coupon'}
        >
          <CouponForm
            onClose={handleCloseAddModal}
            open={addModalOpen}
            editData={editData}
          />
        </EditStoreOwnerModal>
      )}
    </Box>
  );
};

export default CouponList;
